import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Search, Bell, ChevronDown, MessageSquare, User, Mail, MessageCircle,
    Sparkles, Command, Menu, X, LifeBuoy, Sun, Moon,
} from 'lucide-react';
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useTheme } from '../../context/ThemeContext';
import Chat from '../chat/Chat';
import NotificationCenter from '../notifications/NotificationCenter';
import LanguageSelector from './LanguageSelector';
import HelpTicketModal from '../helpdesk/HelpTicketModal';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { baseURL } from '@/config/APIConfig';

const API = baseURL.dashboardApi;
const LOCAL_API = baseURL.local;
const BACKEND_URL = baseURL.live || '';

// ─── Modern Theme Toggle ──────────────────────────────────────────────────────
const ThemeToggle = ({ theme, onToggle }) => {
    const isDark = theme === 'dark';
 
    return (
        <button
            onClick={onToggle}
            data-testid="theme-toggle"
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            className={`
                group flex items-center gap-2 h-9 px-3 rounded-xl
                border transition-all duration-200 select-none outline-none flex-shrink-0
                ${isDark
                    /* active dark — matches LanguageSelector open state */
                    ? 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white shadow-sm'
                    /* idle light */
                    : 'bg-white/60 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700/60 text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 hover:text-slate-900 dark:hover:text-white'
                }
            `}
        >
            {/* Animated icon — swaps between Sun and Moon */}
            <motion.span
                key={isDark ? 'moon' : 'sun'}
                initial={{ rotate: -30, opacity: 0, scale: 0.7 }}
                animate={{ rotate: isDark ? 15 : 0, opacity: 1, scale: 1 }}
                exit={{ rotate: 30, opacity: 0, scale: 0.7 }}
                transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                className={`flex-shrink-0 transition-colors duration-200
                    ${isDark ? 'text-indigo-400' : 'text-amber-500 group-hover:text-amber-600'}`}
            >
                {isDark
                    ? <Moon className="w-[15px] h-[15px]" />
                    : <Sun  className="w-[15px] h-[15px]" />
                }
            </motion.span>
 
            
            <span className={`
                hidden md:block text-[11px] font-bold tracking-widest uppercase
                ${isDark
                    ? 'text-slate-400 dark:text-slate-500'
                    : 'text-slate-400 dark:text-slate-500'
                }
            `}>
                {isDark ? 'Dark' : 'Light'}
            </span>
        </button>
    );
};

// ─── Icon Button ──────────────────────────────────────────────────────────────
const IconBtn = ({ onClick, testId, ariaLabel, badge, children }) => (
    <button
        onClick={onClick}
        data-testid={testId}
        aria-label={ariaLabel}
        className="relative w-9 h-9 rounded-lg
                   bg-slate-100 dark:bg-slate-800
                   text-slate-500 dark:text-slate-400
                   hover:bg-slate-200 dark:hover:bg-slate-700
                   hover:text-slate-900 dark:hover:text-white
                   transition-all duration-150 flex items-center justify-center flex-shrink-0"
    >
        {children}
        {badge > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px]
                             bg-red-500 text-white text-[10px] font-bold
                             rounded-full flex items-center justify-center px-1 ring-2
                             ring-white dark:ring-slate-900">
                {badge > 9 ? '9+' : badge}
            </span>
        )}
    </button>
);

// ─── TopBar ───────────────────────────────────────────────────────────────────
const TopBar = ({ onChatToggle, onMenuToggle, isSidebarOpen }) => {
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [emailCount, setEmailCount] = useState(0);
    const [newEmails, setNewEmails] = useState([]);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [unreadChatCount, setUnreadChatCount] = useState(0);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [showNotificationCenter, setShowNotificationCenter] = useState(false);
    const [notificationCount, setNotificationCount] = useState(0);
    const [showHelpDesk, setShowHelpDesk] = useState(false);

    // ── Auth ──────────────────────────────────────────────────────────────────
    const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? '[{}]');
    const userName = authUser?.username || 'User';
    const userInitials = userName.slice(0, 2).toUpperCase();

    const _logout = () => {
        localStorage.clear();
        window.location.href = '/';
    };

    // ── Notification polling ──────────────────────────────────────────────────
    useEffect(() => {
        const fetchNotificationCount = async () => {
            try {
                const response = await axios.get(`${API}/notifications?limit=1`);
                setNotificationCount(response.data.unread_count || 0);
            } catch {
                setNotificationCount(6);
            }
        };
        fetchNotificationCount();
        const interval = setInterval(fetchNotificationCount, 30000);
        return () => clearInterval(interval);
    }, []);

    // ── Unread chat polling ───────────────────────────────────────────────────
    useEffect(() => {
        const checkUnreadMessages = async () => {
            try {
                const currentUser = localStorage.getItem('username') || 'demo-user';
                const lastChecked = localStorage.getItem('chat_last_checked') || new Date(0).toISOString();
                const channelsResponse = await axios.get(`${API}/chat/channels`);
                const channels = channelsResponse.data.channels || [];
                let totalUnread = 0;
                for (const channel of channels) {
                    try {
                        const messagesResponse = await axios.get(`${API}/chat/messages/${channel.id}`);
                        const messages = messagesResponse.data.messages || [];
                        totalUnread += messages.filter(
                            (msg) => new Date(msg.timestamp) > new Date(lastChecked) && msg.sender !== currentUser
                        ).length;
                    } catch (err) { /* ignore per-channel errors */ }
                }
                setUnreadChatCount(totalUnread);
            } catch { /* ignore */ }
        };
        checkUnreadMessages();
        const interval = setInterval(checkUnreadMessages, 5000);
        return () => clearInterval(interval);
    }, []);

    // ── Email polling ─────────────────────────────────────────────────────────
    useEffect(() => {
        checkNewEmails();
        const interval = setInterval(checkNewEmails, 120000);
        return () => clearInterval(interval);
    }, []);

    const checkNewEmails = async () => {
        try {
            const response = await axios.get(`${API}/mailbox/check-new`);
            if (response.data.new_emails > 0 && response.data.new_emails > emailCount) {
                const newCount = response.data.new_emails - emailCount;
                if (emailCount > 0) {
                    toast.success(`${newCount} new email${newCount > 1 ? 's' : ''} received!`, {
                        icon: <Mail className="w-5 h-5" />,
                    });
                }
            }
            setEmailCount(response.data.new_emails);
            setNewEmails(response.data.emails);
        } catch { /* ignore */ }
    };

    return (
    
    <>
        <motion.header
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl
                       border-b border-slate-200 dark:border-slate-800
                       sticky top-0 z-40"
            data-testid="topbar"
        >
            <div className="h-14 flex items-center px-3 sm:px-4 lg:px-6 gap-3">

                {/* ── Hamburger (mobile only) ── */}
                <button
                    onClick={onMenuToggle}
                    className="lg:hidden w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0
                               bg-slate-100 dark:bg-slate-800
                               text-slate-500 dark:text-slate-400
                               hover:bg-slate-200 dark:hover:bg-slate-700
                               hover:text-slate-900 dark:hover:text-white transition-colors"
                    aria-label="Toggle menu"
                >
                    {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>

               
                <div className="relative flex-1 max-w-xl  group">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 via-[#00C6FB] to-blue-600
                                    rounded-xl opacity-0 group-hover:opacity-100 blur
                                    transition-all duration-500 hidden sm:block pointer-events-none" />
                    <div className="relative flex items-center
                                    bg-slate-100 dark:bg-slate-800
                                    border border-slate-200 dark:border-slate-700
                                    rounded-xl overflow-hidden
                                    group-hover:shadow-sm transition-all duration-200">
                        <div className="w-10 flex items-center justify-center flex-shrink-0">
                            <Search className="w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search bookings, BL#, customers…"
                            data-testid="global-search"
                            onFocus={() => setIsSearchFocused(true)}
                            onBlur={() => setIsSearchFocused(false)}
                            className="flex-1 bg-transparent py-2.5 pr-2 text-sm
                                       text-slate-900 dark:text-white
                                       placeholder-slate-400 dark:placeholder-slate-500
                                       border-none focus:outline-none focus:ring-0 min-w-0"
                        />
                        <div className="hidden sm:flex items-center gap-2 pr-3 flex-shrink-0">
                            <div className="flex items-center gap-1 px-2 py-0.5
                                            bg-blue-50 dark:bg-blue-900/30
                                            border border-blue-100 dark:border-blue-800
                                            rounded-md">
                                <Sparkles className="w-3 h-3 text-blue-500" />
                                <span className="text-[11px] font-semibold text-blue-600 dark:text-blue-400">AI</span>
                            </div>
                            <div className="hidden md:flex items-center gap-0.5 px-1.5 py-0.5
                                            bg-slate-200 dark:bg-slate-700 rounded-md">
                                <Command className="w-3 h-3 text-slate-400" />
                                <span className="text-[11px] text-slate-400 font-medium">K</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Right: all buttons pinned to the right ── */}
                <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 ml-auto">

                    {/* AI Chat */}
                    <IconBtn onClick={onChatToggle} testId="ai-chat-toggle" ariaLabel="AI assistant">
                        <MessageSquare className="w-[18px] h-[18px]" />
                    </IconBtn>

                    <div className="h-5 w-px bg-slate-200 dark:bg-slate-700 mx-0.5" />

                    {/* Help Desk */}
                    <IconBtn onClick={() => setShowHelpDesk(true)} testId="help-desk-toggle" ariaLabel="Help Desk">
                        <LifeBuoy className="w-[18px] h-[18px]" />
                    </IconBtn>

                    {/* Team Chat */}
                    <IconBtn onClick={() => setIsChatOpen(true)} testId="team-chat-toggle" ariaLabel="Team chat" badge={unreadChatCount}>
                        <MessageCircle className="w-[18px] h-[18px]" />
                    </IconBtn>

                    {/* Notifications */}
                    <div className="relative flex-shrink-0">
                        <IconBtn
                            onClick={() => setShowNotificationCenter((v) => !v)}
                            testId="notifications-btn"
                            ariaLabel="Notifications"
                            badge={notificationCount}
                        >
                            <Bell className="w-[18px] h-[18px]" />
                        </IconBtn>
                        <NotificationCenter
                            isOpen={showNotificationCenter}
                            onClose={() => setShowNotificationCenter(false)}
                            onUnreadCountChange={setNotificationCount}
                        />
                    </div>

                    {/* Language */}
                    <LanguageSelector />

                    {/* Theme Toggle */}
                    <ThemeToggle theme={theme} onToggle={toggleTheme} />

                    <div className="h-5 w-px bg-slate-200 dark:bg-slate-700 flex-shrink-0" />

                    {/* User Profile */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                data-testid="user-profile-btn"
                                className="flex items-center gap-2 pl-1 pr-1.5 py-1 rounded-lg
                                           hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0"
                            >
                                <div className="w-8 h-8 rounded-full flex items-center justify-center
                                                bg-gradient-to-br from-blue-600 to-[#00C6FB]
                                                text-white text-[11px] font-bold tracking-wide">
                                    {userInitials}
                                </div>
                                <div className="hidden md:block leading-tight text-left">
                                    <div className="text-[12px] font-semibold text-slate-800 dark:text-slate-100 truncate max-w-[90px]">
                                        {userName}
                                    </div>
                                    <div className="text-[10px] text-slate-400 dark:text-slate-500">Ops Manager</div>
                                </div>
                                <ChevronDown className="w-3.5 h-3.5 text-slate-400 hidden md:block flex-shrink-0" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-48 sm:w-56">
                            <DropdownMenuLabel>My Account</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem>Profile</DropdownMenuItem>
                            <DropdownMenuItem>Settings</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={_logout} className="text-red-600 focus:text-red-600 cursor-pointer">
                                Logout
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

            </div>
        </motion.header>

        {/* ── Modals ── */}
        <Chat
            isOpen={isChatOpen}
            onClose={() => setIsChatOpen(false)}
            onUnreadCountChange={setUnreadChatCount}
        />
        <HelpTicketModal
            isOpen={showHelpDesk}
            onClose={() => setShowHelpDesk(false)}
        />
    </>
    );
};

export default TopBar;